import React, { useState, useMemo } from 'react';
import './Scoreboard.css';

const Scoreboard = ({ whiskies, ratings }) => {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'stats'
  const [showComments, setShowComments] = useState(true);

  // Cookie utility functions
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  };

  // Initialize sortBy from cookie or default to 'total'
  const [sortBy, setSortBy] = useState(() => {
    return getCookie('scoreboard_sort_preference') || 'total';
  });

  // Calculate comprehensive statistics
  const stats = useMemo(() => {
    const ratedWhiskies = whiskies.filter(whisky => ratings[whisky.id]);
    const totalRatings = ratedWhiskies.length;
    const totalWhiskies = whiskies.length;
    
    if (totalRatings === 0) {
      return {
        totalRatings: 0,
        totalWhiskies,
        averageScore: 0,
        highestScore: 0,
        lowestScore: 0,
        categoryAverages: { nose: 0, palate: 0, finish: 0, overall: 0 },
        regionStats: {},
        distilleryStats: {},
        ageStats: {},
        typeStats: {},
        topPerformers: { regions: [], distilleries: [], ages: [], types: [] }
      };
    }

    const allScores = ratedWhiskies.map(whisky => {
      const rating = ratings[whisky.id];
      return rating.nose + rating.palate + rating.finish + rating.overall;
    });

    const categoryTotals = ratedWhiskies.reduce((acc, whisky) => {
      const rating = ratings[whisky.id];
      acc.nose += rating.nose;
      acc.palate += rating.palate;
      acc.finish += rating.finish;
      acc.overall += rating.overall;
      return acc;
    }, { nose: 0, palate: 0, finish: 0, overall: 0 });

    // Calculate statistics by region
    const regionStats = {};
    ratedWhiskies.forEach(whisky => {
      const region = whisky.region || 'Unknown';
      const rating = ratings[whisky.id];
      const totalScore = rating.nose + rating.palate + rating.finish + rating.overall;
      
      if (!regionStats[region]) {
        regionStats[region] = {
          count: 0,
          totalScore: 0,
          scores: [],
          categoryTotals: { nose: 0, palate: 0, finish: 0, overall: 0 }
        };
      }
      
      regionStats[region].count++;
      regionStats[region].totalScore += totalScore;
      regionStats[region].scores.push(totalScore);
      regionStats[region].categoryTotals.nose += rating.nose;
      regionStats[region].categoryTotals.palate += rating.palate;
      regionStats[region].categoryTotals.finish += rating.finish;
      regionStats[region].categoryTotals.overall += rating.overall;
    });

    // Calculate averages for regions
    Object.keys(regionStats).forEach(region => {
      const stat = regionStats[region];
      stat.averageScore = stat.totalScore / stat.count;
      stat.categoryAverages = {
        nose: stat.categoryTotals.nose / stat.count,
        palate: stat.categoryTotals.palate / stat.count,
        finish: stat.categoryTotals.finish / stat.count,
        overall: stat.categoryTotals.overall / stat.count
      };
    });

    // Calculate statistics by distillery
    const distilleryStats = {};
    ratedWhiskies.forEach(whisky => {
      const distillery = whisky.distillery || 'Unknown';
      const rating = ratings[whisky.id];
      const totalScore = rating.nose + rating.palate + rating.finish + rating.overall;
      
      if (!distilleryStats[distillery]) {
        distilleryStats[distillery] = {
          count: 0,
          totalScore: 0,
          scores: [],
          region: whisky.region || 'Unknown'
        };
      }
      
      distilleryStats[distillery].count++;
      distilleryStats[distillery].totalScore += totalScore;
      distilleryStats[distillery].scores.push(totalScore);
    });

    Object.keys(distilleryStats).forEach(distillery => {
      const stat = distilleryStats[distillery];
      stat.averageScore = stat.totalScore / stat.count;
    });

    // Calculate statistics by age
    const ageStats = {};
    ratedWhiskies.forEach(whisky => {
      const age = whisky.age || 'NAS (No Age Statement)';
      const rating = ratings[whisky.id];
      const totalScore = rating.nose + rating.palate + rating.finish + rating.overall;
      
      if (!ageStats[age]) {
        ageStats[age] = {
          count: 0,
          totalScore: 0,
          scores: []
        };
      }
      
      ageStats[age].count++;
      ageStats[age].totalScore += totalScore;
      ageStats[age].scores.push(totalScore);
    });

    Object.keys(ageStats).forEach(age => {
      const stat = ageStats[age];
      stat.averageScore = stat.totalScore / stat.count;
    });

    // Calculate statistics by type
    const typeStats = {};
    ratedWhiskies.forEach(whisky => {
      const type = whisky.type || 'Unknown';
      const rating = ratings[whisky.id];
      const totalScore = rating.nose + rating.palate + rating.finish + rating.overall;
      
      if (!typeStats[type]) {
        typeStats[type] = {
          count: 0,
          totalScore: 0,
          scores: []
        };
      }
      
      typeStats[type].count++;
      typeStats[type].totalScore += totalScore;
      typeStats[type].scores.push(totalScore);
    });

    Object.keys(typeStats).forEach(type => {
      const stat = typeStats[type];
      stat.averageScore = stat.totalScore / stat.count;
    });

    // Find top performers (minimum 2 ratings to qualify)
    const topRegions = Object.entries(regionStats)
      .filter(([_, stat]) => stat.count >= 2)
      .sort(([_, a], [__, b]) => b.averageScore - a.averageScore)
      .slice(0, 5);

    const topDistilleries = Object.entries(distilleryStats)
      .filter(([_, stat]) => stat.count >= 2)
      .sort(([_, a], [__, b]) => b.averageScore - a.averageScore)
      .slice(0, 5);

    const topAges = Object.entries(ageStats)
      .filter(([_, stat]) => stat.count >= 2)
      .sort(([_, a], [__, b]) => b.averageScore - a.averageScore)
      .slice(0, 5);

    const topTypes = Object.entries(typeStats)
      .filter(([_, stat]) => stat.count >= 2)
      .sort(([_, a], [__, b]) => b.averageScore - a.averageScore)
      .slice(0, 5);

    return {
      totalRatings,
      totalWhiskies,
      averageScore: allScores.reduce((a, b) => a + b, 0) / totalRatings,
      highestScore: Math.max(...allScores),
      lowestScore: Math.min(...allScores),
      categoryAverages: {
        nose: categoryTotals.nose / totalRatings,
        palate: categoryTotals.palate / totalRatings,
        finish: categoryTotals.finish / totalRatings,
        overall: categoryTotals.overall / totalRatings
      },
      regionStats,
      distilleryStats,
      ageStats,
      typeStats,
      topPerformers: {
        regions: topRegions,
        distilleries: topDistilleries,
        ages: topAges,
        types: topTypes
      }
    };
  }, [whiskies, ratings]);

  // Get sorted rated whiskies
  const sortedRatedWhiskies = useMemo(() => {
    const ratedWhiskies = whiskies
      .filter(whisky => ratings[whisky.id])
      .map(whisky => {
        const rating = ratings[whisky.id];
        const total = rating.nose + rating.palate + rating.finish + rating.overall;
        return {
          ...whisky,
          rating,
          total,
          average: total / 4
        };
      });

    switch (sortBy) {
      case 'total':
        return ratedWhiskies.sort((a, b) => b.total - a.total);
      case 'nose':
        return ratedWhiskies.sort((a, b) => b.rating.nose - a.rating.nose);
      case 'palate':
        return ratedWhiskies.sort((a, b) => b.rating.palate - a.rating.palate);
      case 'finish':
        return ratedWhiskies.sort((a, b) => b.rating.finish - a.rating.finish);
      case 'overall':
        return ratedWhiskies.sort((a, b) => b.rating.overall - a.rating.overall);
      case 'name':
        return ratedWhiskies.sort((a, b) => a.name.localeCompare(b.name));
      case 'recent':
        return ratedWhiskies.sort((a, b) => 
          new Date(b.rating.timestamp) - new Date(a.rating.timestamp)
        );
      default:
        return ratedWhiskies;
    }
  }, [whiskies, ratings, sortBy]);

  const getRankIcon = (index) => {
    switch (index) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return `#${index + 1}`;
    }
  };

  const getScoreColor = (score, maxScore = 25) => {
    const percentage = score / maxScore;
    if (percentage >= 0.9) return '#4CAF50'; // 90%+ - Excellent (22.5+/25 or 90+/100)
    if (percentage >= 0.8) return '#8BC34A'; // 80%+ - Very Good (20+/25 or 80+/100)
    if (percentage >= 0.7) return '#CDDC39'; // 70%+ - Good (17.5+/25 or 70+/100)
    if (percentage >= 0.6) return '#FFC107'; // 60%+ - Above Average (15+/25 or 60+/100)
    if (percentage >= 0.5) return '#FF9800'; // 50%+ - Average (12.5+/25 or 50+/100)
    if (percentage >= 0.4) return '#FF5722'; // 40%+ - Below Average (10+/25 or 40+/100)
    return '#F44336'; // <40% - Poor
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCookie('scoreboard_sort_preference', newSortBy);
  };

  const exportToCSV = () => {
    const ratedWhiskies = whiskies.filter(whisky => ratings[whisky.id]);
    
    if (ratedWhiskies.length === 0) {
      alert('No ratings to export!');
      return;
    }

    // CSV headers
    const headers = [
      'Name',
      'Distillery', 
      'Region',
      'Age',
      'ABV',
      'Type',
      'Stand',
      'Dream Dram',
      'Dream Dram Tokens',
      'Nose Score',
      'Palate Score', 
      'Finish Score',
      'Overall Score',
      'Total Score',
      'Comments',
      'Rating Date'
    ];

    // Convert data to CSV rows
    const csvData = ratedWhiskies.map(whisky => {
      const rating = ratings[whisky.id];
      const totalScore = rating.nose + rating.palate + rating.finish + rating.overall;
      
      return [
        `"${whisky.name || ''}"`,
        `"${whisky.distillery || ''}"`,
        `"${whisky.region || ''}"`,
        `"${whisky.age || ''}"`,
        `"${whisky.abv || ''}"`,
        `"${whisky.type || ''}"`,
        `"${whisky.stand || ''}"`,
        whisky.dreamDram ? 'Yes' : 'No',
        whisky.dreamDramTokens || '',
        rating.nose,
        rating.palate,
        rating.finish,
        rating.overall,
        totalScore,
        `"${rating.comments || ''}"`,
        rating.timestamp ? new Date(rating.timestamp).toLocaleString() : ''
      ];
    });

    // Create CSV content
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `whisky-ratings-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (stats.totalRatings === 0) {
    return (
      <div className="scoreboard">
        <div className="empty-state">
          <h3>No Ratings Yet</h3>
          <p>Start rating whiskies to see your scoreboard!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scoreboard">
      <div className="scoreboard-header">
        <h2>🏆 Scoreboard</h2>
        <div className="header-controls">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              className={`toggle-btn ${viewMode === 'stats' ? 'active' : ''}`}
              onClick={() => setViewMode('stats')}
            >
              Stats
            </button>
          </div>
          <button 
            className={`toggle-button ${showComments ? 'active' : ''}`}
            onClick={() => setShowComments(!showComments)}
            title="Toggle comments display"
          >💭
          </button>
          <button 
            className="export-button"
            onClick={exportToCSV}
            title="Export ratings to CSV"
          >
            📊
          </button>
        </div>
      </div>

      {viewMode === 'stats' ? (
        <div className="stats-view">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{stats.totalRatings}</div>
              <div className="stat-label">Whiskies Rated</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.averageScore.toFixed(1)}</div>
              <div className="stat-label">Average Score</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.highestScore}</div>
              <div className="stat-label">Highest Score</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{Math.round((stats.totalRatings / stats.totalWhiskies) * 100)}%</div>
              <div className="stat-label">Progress</div>
            </div>
          </div>

          <div className="category-stats">
            <h3>Category Averages</h3>
            <div className="category-bars">
              {Object.entries(stats.categoryAverages).map(([category, average]) => (
                <div key={category} className="category-bar">
                  <div className="category-info">
                    <span className="category-name">{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                    <span className="category-score">{average.toFixed(1)}/25</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${(average / 25) * 100}%`,
                        backgroundColor: getScoreColor(average)
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Regional Statistics */}
          {Object.keys(stats.regionStats).length > 0 && (
            <div className="detailed-stats">
              <h3>📍 Average Ratings by Region</h3>
              <div className="stats-list">
                {Object.entries(stats.regionStats)
                  .sort(([_, a], [__, b]) => b.averageScore - a.averageScore)
                  .map(([region, stat]) => (
                    <div key={region} className="stat-item">
                      <div className="stat-item-header">
                        <span className="stat-item-name">{region}</span>
                        <div className="stat-item-values">
                          <span className="stat-item-score" style={{ color: getScoreColor(stat.averageScore, 100) }}>
                            {stat.averageScore.toFixed(1)}
                          </span>
                          <span className="stat-item-count">({stat.count} whisky{stat.count !== 1 ? 'ies' : ''})</span>
                        </div>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${(stat.averageScore / 100) * 100}%`,
                            backgroundColor: getScoreColor(stat.averageScore, 100)
                          }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Distillery Statistics */}
          {Object.keys(stats.distilleryStats).length > 0 && (
            <div className="detailed-stats">
              <h3>🏭 Top Distilleries by Average Rating</h3>
              <div className="stats-list">
                {Object.entries(stats.distilleryStats)
                  .sort(([_, a], [__, b]) => b.averageScore - a.averageScore)
                  .slice(0, 10)
                  .map(([distillery, stat]) => (
                    <div key={distillery} className="stat-item">
                      <div className="stat-item-header">
                        <div>
                          <span className="stat-item-name">{distillery}</span>
                          <span className="stat-item-region">({stat.region})</span>
                        </div>
                        <div className="stat-item-values">
                          <span className="stat-item-score" style={{ color: getScoreColor(stat.averageScore, 100) }}>
                            {stat.averageScore.toFixed(1)}
                          </span>
                          <span className="stat-item-count">({stat.count} whisky{stat.count !== 1 ? 'ies' : ''})</span>
                        </div>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${(stat.averageScore / 100) * 100}%`,
                            backgroundColor: getScoreColor(stat.averageScore, 100)
                          }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Age Statistics */}
          {Object.keys(stats.ageStats).length > 0 && (
            <div className="detailed-stats">
              <h3>🕰️ Average Ratings by Age</h3>
              <div className="stats-list">
                {Object.entries(stats.ageStats)
                  .sort(([_, a], [__, b]) => b.averageScore - a.averageScore)
                  .map(([age, stat]) => (
                    <div key={age} className="stat-item">
                      <div className="stat-item-header">
                        <span className="stat-item-name">{age}</span>
                        <div className="stat-item-values">
                          <span className="stat-item-score" style={{ color: getScoreColor(stat.averageScore, 100) }}>
                            {stat.averageScore.toFixed(1)}
                          </span>
                          <span className="stat-item-count">({stat.count} whisky{stat.count !== 1 ? 'ies' : ''})</span>
                        </div>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${(stat.averageScore / 100) * 100}%`,
                            backgroundColor: getScoreColor(stat.averageScore, 100)
                          }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Type Statistics */}
          {Object.keys(stats.typeStats).length > 0 && (
            <div className="detailed-stats">
              <h3>🥃 Average Ratings by Type</h3>
              <div className="stats-list">
                {Object.entries(stats.typeStats)
                  .sort(([_, a], [__, b]) => b.averageScore - a.averageScore)
                  .map(([type, stat]) => (
                    <div key={type} className="stat-item">
                      <div className="stat-item-header">
                        <span className="stat-item-name">{type}</span>
                        <div className="stat-item-values">
                          <span className="stat-item-score" style={{ color: getScoreColor(stat.averageScore, 100) }}>
                            {stat.averageScore.toFixed(1)}
                          </span>
                          <span className="stat-item-count">({stat.count} whisky{stat.count !== 1 ? 'ies' : ''})</span>
                        </div>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${(stat.averageScore / 100) * 100}%`,
                            backgroundColor: getScoreColor(stat.averageScore, 100)
                          }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Top Performers Summary */}
          {(stats.topPerformers.regions.length > 0 || stats.topPerformers.distilleries.length > 0) && (
            <div className="top-performers">
              <h3>🏆 Top Performers</h3>
              <div className="top-performers-grid">
                {stats.topPerformers.regions.length > 0 && (
                  <div className="top-performer-category">
                    <h4>Best Regions</h4>
                    {stats.topPerformers.regions.slice(0, 3).map(([region, stat], index) => (
                      <div key={region} className="top-performer-item">
                        <span className="performer-rank">{getRankIcon(index)}</span>
                        <span className="performer-name">{region}</span>
                        <span className="performer-score" style={{ color: getScoreColor(stat.averageScore, 100) }}>
                          {stat.averageScore.toFixed(1)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                {stats.topPerformers.distilleries.length > 0 && (
                  <div className="top-performer-category">
                    <h4>Best Distilleries</h4>
                    {stats.topPerformers.distilleries.slice(0, 3).map(([distillery, stat], index) => (
                      <div key={distillery} className="top-performer-item">
                        <span className="performer-rank">{getRankIcon(index)}</span>
                        <span className="performer-name">{distillery}</span>
                        <span className="performer-score" style={{ color: getScoreColor(stat.averageScore, 100) }}>
                          {stat.averageScore.toFixed(1)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="list-view">
          <div className="controls">
            <select
              className="input sort-select"
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
            >
              <option value="total">Sort by Total Score</option>
              <option value="nose">Sort by Nose</option>
              <option value="palate">Sort by Palate</option>
              <option value="finish">Sort by Finish</option>
              <option value="overall">Sort by Overall</option>
              <option value="recent">Sort by Recent</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>

          <div className="rankings">
            {sortedRatedWhiskies.map((whisky, index) => (
              <div 
                key={whisky.id}
                className={`ranking-item ${whisky.dreamDram ? 'dream-dram' : ''}`}
              >
                <div className="rank">
                  <span className="rank-icon">{getRankIcon(index)}</span>
                </div>
                
                <div className="whisky-info">
                  <h4 className="whisky-name">
                    {whisky.name}
                    {whisky.stand && (
                      <span className="stand-badge" title={`Stand ${whisky.stand}`}>
                        📍{whisky.stand}
                      </span>
                    )}
                    {whisky.dreamDram && (
                      <span className="dream-dram-icon" title={`Dream Dram - Requires ${whisky.dreamDramTokens} token${whisky.dreamDramTokens > 1 ? 's' : ''}`}>
                        💎{whisky.dreamDramTokens}
                      </span>
                    )}
                  </h4>
                  {whisky.distillery && (
                    <p className="whisky-distillery">{whisky.distillery}</p>
                  )}
                  <div className="whisky-meta">
                    {whisky.region && <span>{whisky.region}</span>}
                    {whisky.age && <span>{whisky.age}</span>}
                  </div>
                  {showComments && whisky.rating.comments && (
                    <div className="scoreboard-comments">💭 {whisky.rating.comments}
                    </div>
                  )}
                </div>

                <div className="scores">
                  <div className="total-score">
                    <span 
                      className="score-value"
                      style={{ color: getScoreColor(whisky.total, 100) }}
                    >
                      {whisky.total}
                    </span>
                    <span className="score-max">/100</span>
                  </div>
                  <div className="category-scores">
                    <div className="score-item">
                      <span className="score-label">N</span>
                      <span 
                        className="score-value"
                        style={{ color: getScoreColor(whisky.rating.nose) }}
                      >
                        {whisky.rating.nose}
                      </span>
                    </div>
                    <div className="score-item">
                      <span className="score-label">P</span>
                      <span 
                        className="score-value"
                        style={{ color: getScoreColor(whisky.rating.palate) }}
                      >
                        {whisky.rating.palate}
                      </span>
                    </div>
                    <div className="score-item">
                      <span className="score-label">F</span>
                      <span 
                        className="score-value"
                        style={{ color: getScoreColor(whisky.rating.finish) }}
                      >
                        {whisky.rating.finish}
                      </span>
                    </div>
                    <div className="score-item">
                      <span className="score-label">O</span>
                      <span 
                        className="score-value"
                        style={{ color: getScoreColor(whisky.rating.overall) }}
                      >
                        {whisky.rating.overall}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Scoreboard;
